/* oxlint-disable react-hooks/rules-of-hooks -- tracked in docs/oxlint-violations.md */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import type { Primitive } from 'type-fest';

import {
    Body1,
    Body2,
    Button,
    Flex,
    Heading1,
    SelectionCard,
    Stack,
} from '@myphx-engineering/phx-design-system';

import { sendVerificationCodeApi } from '../../api/send-verification-code.api';
import { checkTotpStatusApi } from '../../api/totp.api';
import { PageLayout } from '../../components';
import {
    ErrorMap,
    type ErrorMessageTypes,
} from '../../components/alerts/errors/error-map';
import { telemetryInstance } from '../../instrumentation';
import type { ChallengeQuestionsFormModel } from '../../models/form.models';
import { routes } from '../../routes';

export type MfaSelectPageState = {
    phoneNumber: string;
    password: string;
    callbackUrl: string;
    kratosIdentityId?: string;
    kratosSessionToken?: string;
    isSignupFlow?: boolean;
    signupData?: {
        firstName: string;
        lastName: string;
        email: string;
        dateOfBirth: string;
        userId?: string;
        address?: ChallengeQuestionsFormModel['address'] | null;
        partnerAccountConfiguration?: Record<string, Primitive>;
        brandId?: string;
    };
};

const MfaSelectPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const state = location?.state as MfaSelectPageState;

    // Validate state
    if (!state || !state.kratosSessionToken) {
        navigate({
            pathname: routes.signIn,
            search: window.location.search,
        });
        return null;
    }

    const [loading, setLoading] = useState(false);
    const [totpEnrolled, setTotpEnrolled] = useState<boolean | undefined>();
    const [errorMessageType, setErrorMessageType] = useState<
        ErrorMessageTypes | undefined
    >();
    const [selectedMethod, setSelectedMethod] = useState<'text' | 'totp'>(
        'totp'
    );

    const ErrorMessage = errorMessageType ? ErrorMap[errorMessageType] : null;

    // Check TOTP enrollment status on page load
    useEffect(() => {
        const checkTotpStatus = async () => {
            setLoading(true);
            try {
                const { totpEnrolled: enrolled } = await checkTotpStatusApi(
                    state.kratosSessionToken ?? ''
                );
                setTotpEnrolled(enrolled);
            } catch (error) {
                telemetryInstance.logError({
                    source: location.pathname,
                    exception:
                        error instanceof Error
                            ? error
                            : new Error(String(error)),
                });
            } finally {
                setLoading(false);
            }
        };
        checkTotpStatus();
    }, [state.kratosSessionToken, location.pathname]);

    const selectTotp = () => setSelectedMethod('totp');
    const selectText = () => setSelectedMethod('text');

    const sendVerificationCode = useCallback(
        async (successCb: () => void) => {
            // SMS selected - request OTP and navigate to verify page
            setLoading(true);
            setErrorMessageType(undefined);

            try {
                const { success, error } = await sendVerificationCodeApi(
                    state.phoneNumber
                );

                if (success) {
                    successCb();
                } else {
                    telemetryInstance.logError({
                        source: location.pathname,
                        exception: new Error(
                            `SMS OTP request failed: ${error}`
                        ),
                    });

                    setErrorMessageType(error);
                }
            } catch (error) {
                telemetryInstance.logError({
                    source: location.pathname,
                    exception:
                        error instanceof Error
                            ? error
                            : new Error(String(error)),
                });
                setErrorMessageType('500_GenericError');
            } finally {
                setLoading(false);
            }
        },
        [state, location.pathname]
    );

    const continueWithTotp = async () => {
        // If already enrolled, go directly to verify page
        if (totpEnrolled === true) {
            telemetryInstance.logEvent({
                name: 'MFASelectTOTPVerify',
                source: location.pathname,
            });

            navigate(
                {
                    pathname: routes.totpVerify,
                    search: window.location.search,
                },
                { state }
            );

            return;
        }

        await sendVerificationCode(() => {
            navigate(
                {
                    pathname: routes.totpPreSetup,
                    search: window.location.search,
                },
                { state }
            );
        });
    };

    const continueWithSms = async () => {
        telemetryInstance.logEvent({
            name: 'MFASelectSMS',
            source: location.pathname,
        });

        await sendVerificationCode(() => {
            // Navigate to verify page with OTP flow
            navigate(
                {
                    pathname: routes.verify,
                    search: window.location.search,
                },
                {
                    state: {
                        phoneNumber: state.phoneNumber,
                        password: state.password,
                        callBackUrl: state.callbackUrl,
                        kratosSessionId: state.kratosIdentityId,
                        kratosSessionToken: state.kratosSessionToken,
                        isSignupFlow: false, // Account already exists
                    },
                }
            );
        });
    };

    const handleContinue = async () => {
        if (selectedMethod === 'totp') {
            await continueWithTotp();
        } else {
            await continueWithSms();
        }
    };

    return (
        <PageLayout
            loadingOverlay={loading}
            header={errorMessageType && ErrorMessage && <ErrorMessage />}
            heading={
                <Stack gap="sm">
                    <Heading1>{t('mfaSelectPage.title')}</Heading1>
                    <Body1>{t('mfaSelectPage.description')}</Body1>
                </Stack>
            }
            footer={
                <Button
                    disabled={loading}
                    fullWidth
                    size="lg"
                    onClick={handleContinue}
                >
                    {t('common.continue')}
                </Button>
            }
        >
            <Stack gap="md">
                <SelectionCard
                    color="primary"
                    onClick={loading ? () => {} : selectTotp}
                    isSelected={selectedMethod === 'totp'}
                    header={
                        <Flex justify="space-between" align="center" gap="xs">
                            <Body1 bold>
                                {t('mfaSelectPage.authenticatorApp.title')}
                            </Body1>
                            <Body2 bold={false} color="primary">
                                ({t('mfaSelectPage.recommended')})
                            </Body2>
                        </Flex>
                    }
                >
                    <Body1>
                        {t('mfaSelectPage.authenticatorApp.description')}
                    </Body1>
                </SelectionCard>

                <SelectionCard
                    color="primary"
                    onClick={loading ? () => {} : selectText}
                    isSelected={selectedMethod === 'text'}
                    header={
                        <Body1 bold>
                            {t('mfaSelectPage.textMessage.title')}
                        </Body1>
                    }
                >
                    <Body1>{t('mfaSelectPage.textMessage.description')}</Body1>
                </SelectionCard>
            </Stack>
        </PageLayout>
    );
};

export default MfaSelectPage;
